import { ArrowSquareOutIcon, PencilSimpleLineIcon } from '@phosphor-icons/react';

import {
  EditorialStatusBadge,
  editorialTopicStatus,
} from '@/components/reach/editorial/EditorialPillarSheet';
import { EditorialProgressRing } from '@/components/reach/editorial/EditorialProgressRing';
import type {
  EditorialSort,
  EditorialSortKey,
} from '@/components/reach/editorial/editorial-table-model';
import { Button } from '@/components/ui/button';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ArticlePublicationSummary } from '@/hooks/reach/useArticleHandoff';
import type {
  EditorialArticleScore,
  EditorialWebReadiness,
} from '@/hooks/reach/useEditorialArticleTrail';
import type { EditorialTopicRow } from '@/hooks/reach/useEditorialMap';
import type { ArticleApprovalState } from '@/lib/content-doctrine/article-progress';
import { formatWhen } from '@/lib/format-when';
import {
  ITEM_META,
  ITEM_TITLE,
  ROW_HOVER,
  TABLE_CELL_MUTED,
  TABLE_HEADER_SURFACE,
  TABLE_HEAD_CELL,
  TABLE_IDENTITY_CELL,
  TABLE_IDENTITY_ROW,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { type ArticleReviewJob, isArticleJobActive } from '@/services/data/ArticleJobService';

/**
 * Extends `EditorialTopicRow` rather than the narrower `EditorialTopic`, and
 * adds only the two RESOLVED NAMES the table renders.
 */
export interface EditorialTableRow extends EditorialTopicRow {
  pillarName: string;
  branchName: string | null;
}

/**
 * Every proposed article in one table — what an operator works from, where the
 * map is what a client is shown.
 *
 * ── REBUILT 2026-09-06 AFTER A FOUNDER REVIEW, AND EVERY POINT WAS FAIR ─────
 * _"This looks like crap... we literally have status pills on the other reach
 * pages... What is this write button at the end? That doesn't match anywhere
 * else... If it's written, how do I even see it? There's no way to even click
 * it and see the actual article... There's so much going on on the page, and
 * the cognitive overload is insane."_
 *
 * Four defects, each with its own cause:
 *
 * 1. SEVEN COLUMNS, of which two were unreadable by construction. `Content
 *    pillar` printed the SAME truncated string ("Data Centers & Mission-Critical
 *    I…") on fifteen consecutive rows, and `Primary audience` / `Reference` were
 *    long prose truncated to a fragment. A column whose every visible value is
 *    identical, or elided mid-word, is carrying no information — it is only
 *    charging for width. Down to three: what state it is in, what it is, and
 *    where it is filed. Branch moved to a sub-line under the title, where it
 *    reads as the qualifier it is rather than competing as a peer column.
 *
 * 2. STATUS WAS BARE TEXT while every other Reach table uses the app's pill.
 *    That is drift, the design system's own "#1 smell", and it is why the page
 *    did not look like the rest of the product. `StatusBadge` now, with tone
 *    carrying the meaning: written is `success` because it is the outcome this
 *    surface exists to produce.
 *
 * 3. A `variant="flat"` Write button parked in the corner of all 32 rows. The
 *    design system forbids exactly this ("flat is for tight-packed toolbars,
 *    not solo corners... visible enough to add noise, invisible enough not to
 *    invite the click"), and a permanent per-row button for an action most rows
 *    will never take is 32 invitations to spend tokens. It is now a
 *    hover-revealed icon, the pattern the same rule prescribes.
 *
 * 4. 🔴 A WRITTEN ARTICLE WAS A DEAD END. The row said "Written" — twice, once
 *    in Status and again in a trailing cell — and offered no way to read the
 *    thing. Worse than a missing feature: the surface asserted a document
 *    existed and then withheld it. Clicking a written row now opens it in the
 *    Inspector, the same floating panel every other artifact in the app opens
 *    in. See `useOpenEditorialArticle` for why that needed a data path rather
 *    than a click handler.
 *
 * SORT AND PAGE SIZE (2026-10-15). Article, Status and Last changed sort through the app's one
 * `SortableTableHead`; the page owns the sort and slices AFTER it, because sorting a visible page of
 * ten would rank an arbitrary ten. Rows per page is `TablePagination`'s own `showPageSize`. The sort,
 * the filter and the Status column all read `editorial-table-model.ts`.
 */
export function EditorialTopicTable({
  rows,
  onWrite,
  onOpenTopic,
  scores,
  publications,
  approvals,
  changedAt,
  sort = null,
  onSort,
  jobsByTopic,
  pendingTopicId,
  canStart = true,
}: {
  rows: EditorialTableRow[];
  jobsByTopic?: Map<string, ArticleReviewJob>;
  pendingTopicId?: string | null;
  canStart?: boolean;
  /** Opens the blog-writer with this topic's filing. Omit to hide the action. */
  onWrite?: (row: EditorialTableRow) => void;
  /**
   * Opens this topic's detail dock. EVERY row, not just written ones: the panel
   * answers "what is this and what has it been through", which a proposed topic
   * has an answer to as well. Reading the prose is a second, deliberate step
   * from inside the panel — a 1,400-word article does not belong in a rail.
   */
  onOpenTopic?: (row: EditorialTableRow) => void;
  /**
   * Publish-readiness per artifact id, from `useEditorialArticleScores` — one
   * query for the whole table. A row with no entry renders `unknown`, which is
   * the correct reading: nothing has measured it.
   */
  scores?: Map<string, EditorialArticleScore>;
  /**
   * The latest non-cancelled `external_publications` row per topic id, from
   * `useArticlePublicationStates` — one query for the whole table, same shape
   * as `scores`. A row with no entry has never been handed off.
   */
  publications?: Map<string, ArticlePublicationSummary>;
  /**
   * Client-approval state of the CURRENT revision per topic id, from
   * `useArticleApprovalStates`. Feeds the Progress ring's third segment.
   */
  approvals?: Map<string, ArticleApprovalState>;
  /** Last change per ARTIFACT id, from `useEditorialArticleChangedAt`. A row with no entry reads "—". */
  changedAt?: Map<string, string>;
  /** The active sort, or null for the order the rows arrived in. */
  sort?: EditorialSort | null;
  /** Omit and the headers render as plain labels. */
  onSort?: (key: EditorialSortKey) => void;
}): JSX.Element {
  return (
    <Table>
      <TableHeader className={TABLE_HEADER_SURFACE}>
        <TableRow data-header>
          <SortableTableHead<EditorialSortKey>
            label="Article"
            align="left"
            sortKey="article"
            activeSort={sort?.key ?? null}
            direction={sort?.direction}
            onSort={onSort}
          />
          <SortableTableHead<EditorialSortKey>
            label="Status"
            align="left"
            sortKey="status"
            activeSort={sort?.key ?? null}
            direction={sort?.direction}
            onSort={onSort}
            className="hidden w-36 lg:table-cell"
          />
          <SortableTableHead<EditorialSortKey>
            label="Progress"
            align="left"
            className="hidden w-20 lg:table-cell"
          />
          <SortableTableHead<EditorialSortKey>
            label="Last changed"
            align="left"
            sortKey="changed"
            activeSort={sort?.key ?? null}
            direction={sort?.direction}
            onSort={onSort}
            className="hidden w-32 lg:table-cell"
          />
          <SortableTableHead<EditorialSortKey>
            label="Content pillar"
            align="left"
            className="hidden w-64 max-w-64 lg:table-cell"
          />
          <TableHead className={cn(TABLE_HEAD_CELL, 'w-12 text-right')}>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const canOpen = Boolean(onOpenTopic);
          const job = jobsByTopic?.get(row.id);
          const activeJob = isArticleJobActive(job);
          const score = row.artifactId ? (scores?.get(row.artifactId) ?? null) : null;
          const publication = publications?.get(row.id) ?? null;
          const approval = approvals?.get(row.id) ?? null;
          const status = editorialTopicStatus(row, job, score, publication);
          const statusId = status.id;
          const changed = row.artifactId ? (changedAt?.get(row.artifactId) ?? null) : null;
          return (
            <TableRow
              key={row.id}
              // The whole row is the action when there is something to read —
              // the design system's rule for a single primary action. It is NOT
              // clickable when nothing has been written, because a row that
              // looks clickable and does nothing is worse than a plain row.
              // `group` unconditionally: the hover-revealed Write action lives on
              // rows that are NOT openable, so gating the group on `canOpen`
              // left it `opacity-0` with nothing to ever reveal it — an
              // invisible button on all 31 unwritten rows.
              className={cn(TABLE_IDENTITY_ROW, ROW_HOVER, 'group', canOpen && 'cursor-pointer')}
              onClick={canOpen ? () => onOpenTopic?.(row) : undefined}
              role={canOpen ? 'button' : undefined}
              tabIndex={canOpen ? 0 : undefined}
              onKeyDown={
                canOpen
                  ? (event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onOpenTopic?.(row);
                      }
                    }
                  : undefined
              }
              aria-label={canOpen ? `Open the topic "${row.topic}"` : undefined}
              aria-describedby={row.artifactId ? `progress-${row.id}` : undefined}
            >
              <TableCell className={TABLE_IDENTITY_CELL}>
                <p className={cn(ITEM_TITLE, 'line-clamp-2')} title={row.topic}>
                  {row.topic}
                </p>
                {/* The branch qualifies the title; it was a column of its own
                    and read as a peer of the article name, which it is not. */}
                <div className={cn(ITEM_META, 'flex flex-wrap items-center gap-x-2 gap-y-1')}>
                  <span>{row.branchName ?? 'Not filed to a branch'}</span>
                  {/* 🔴 BELOW `lg` THESE STACK UNDER THE TITLE INSTEAD OF
                      STANDING AS COLUMNS. Measured at 390px: a fixed-width
                      status pill and the score column took 172 of the row's
                      356px, leaving the ARTICLE TITLE — the only thing a
                      reader is actually scanning for — 120px, which truncated
                      every row to two or three words ("What Qualifies for the
                      Texas…"). Four columns is simply the wrong shape at phone
                      width; the fix is to change the shape, not to shave a
                      few pixels off each column and call it responsive. */}
                  <span className="flex items-center gap-2 lg:hidden">
                    <EditorialStatusBadge status={status} />
                    {row.artifactId ? (
                      <EditorialProgressRing
                        hasArticle
                        statusId={statusId}
                        score={score}
                        approval={approval}
                        publication={publication}
                      />
                    ) : null}
                  </span>
                </div>
              </TableCell>
              <TableCell className={cn(TABLE_IDENTITY_CELL, 'hidden w-36 lg:table-cell')}>
                <EditorialStatusBadge status={status} />
              </TableCell>
              <TableCell className={cn(TABLE_IDENTITY_CELL, 'hidden w-20 lg:table-cell')}>
                {row.artifactId ? (
                  <EditorialProgressRing
                    hasArticle
                    statusId={statusId}
                    describedById={`progress-${row.id}`}
                    className="flex"
                    score={score}
                    approval={approval}
                    publication={publication}
                  />
                ) : null}
              </TableCell>
              <TableCell
                className={cn(
                  TABLE_IDENTITY_CELL,
                  TABLE_CELL_MUTED,
                  'hidden w-32 whitespace-nowrap tabular-nums lg:table-cell',
                )}
              >
                {changed ? (
                  <time dateTime={changed} title={absoluteDate(changed)}>
                    {formatWhen(changed)}
                  </time>
                ) : (
                  <span aria-label="Not written yet">—</span>
                )}
              </TableCell>
              <TableCell
                className={cn(
                  TABLE_IDENTITY_CELL,
                  TABLE_CELL_MUTED,
                  'hidden max-w-64 truncate lg:table-cell',
                )}
                title={row.pillarName}
              >
                {row.pillarName}
              </TableCell>
              <TableCell className={cn(TABLE_IDENTITY_CELL, 'w-12 text-right')}>
                {row.hasArticle ? (
                  <ArrowSquareOutIcon
                    weight="regular"
                    className="ml-auto size-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                    aria-hidden
                  />
                ) : onWrite ? (
                  <Button
                    variant="flat"
                    size="icon-sm"
                    disabled={!canStart || activeJob || pendingTopicId === row.id}
                    // 🔴 VISIBLE BY DEFAULT BELOW `lg`, HOVER-REVEALED ABOVE
                    // IT. A touch screen has no hover, so `opacity-0
                    // group-hover:opacity-100` made this control permanently
                    // invisible AND permanently unreachable on a phone —
                    // while the topic dock told the reader "Use Write on the
                    // row to start", pointing at something that could never
                    // appear. The desktop restraint the design system asks
                    // for (no permanent per-row button) is kept where hover
                    // exists to earn it.
                    className="-mr-1 transition-opacity focus-visible:opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                    onClick={(event) => {
                      event.stopPropagation();
                      onWrite(row);
                    }}
                    aria-label={`Write the article "${row.topic}"`}
                    title="Write this article"
                  >
                    <PencilSimpleLineIcon weight="regular" />
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function absoluteDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * A mechanical check result, never a grade for the writing.
 *
 * `compact` drops the score/applied-checks suffix, keeping only the state
 * word — the table's `w-36` (144px) "Draft checks" column has no room for
 * "Needs attention · 85 · 12/12" and was wrapping it to two lines. The side
 * panel's own `Draft checks` section has the width for the full detail, so
 * it stays on the default (non-compact) rendering.
 */
export function EditorialScore({
  readiness,
  hasArticle,
  compact = false,
  job,
}: {
  readiness: EditorialWebReadiness | null;
  hasArticle: boolean;
  compact?: boolean;
  job?: ArticleReviewJob;
}): JSX.Element | null {
  if (!hasArticle) return null;
  // A claim check writes its own record and leaves the mechanical checks as they were.
  const activeJob = isArticleJobActive(job) && job?.operation !== 'factcheck';
  const activeStage = (job?.currentStage ?? '').replace(/_\d+$/, '');
  const checking = ['review', 'critique', 'validate', 'checks'].includes(activeStage);
  const state = activeJob
    ? checking
      ? 'Checking'
      : 'Waiting'
    : !readiness
      ? 'Not checked'
      : !readiness.isCurrent
        ? 'Outdated'
        : readiness.ready
          ? 'Passed'
          : 'Needs attention';
  const coverage = readiness
    ? `${readiness.applied} run${readiness.total > readiness.applied ? ` · ${readiness.total - readiness.applied} not applicable` : ''}`
    : '';
  return (
    <StatusBadge
      tone={
        state === 'Passed'
          ? 'success'
          : state === 'Needs attention'
            ? 'warning'
            : state === 'Checking'
              ? 'info'
              : 'neutral'
      }
      fill="outline"
      className="whitespace-nowrap"
    >
      {!activeJob && !compact && (state === 'Passed' || state === 'Needs attention')
        ? `${state} · ${readiness!.score} · ${coverage}`
        : state}
    </StatusBadge>
  );
}
